import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchRecommendedUsers, 
  selectRecommendedUsers, 
  selectUserInfo, 
  getApiBaseUrl 
} from '../../../redux/WithcookieSlice';
import { RecommendedUsersProps } from './RecommendedUsers.types';
import { adaptRecommendedUsersProps } from './RecommendedUsers.adapter';
import './RecommendedUsers.css';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * 날짜 형식화 함수
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// 전역 중복 방지 맵 - 컴포넌트 인스턴스별로 관리
const globalFetchMap = new Map<string, boolean>();

/**
 * RecommendedUsers 컴포넌트
 */
const RecommendedUsersComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환 - props 안정화
  const adaptedProps = useMemo(() => {
    console.log(`[RecommendedUsers] props 변경 감지`, props);
    return adaptRecommendedUsersProps(props);
  }, [props]);
  const {
    style = {},
    className = '',
    componentProps = {},
    isPreview = false,
    id = '',
    items: propItems,
    loading: propLoading,
    error: propError,
    currentPage: propCurrentPage,
    totalCount: propTotalCount,
    limit: propLimit,
    pathVariables = {},
    content,
    data,
    ...htmlProps
  } = adaptedProps;

  // Redux 타입 에러 해결을 위해 any 타입 단언 사용
  const dispatch = useDispatch() as any;
  
  // Redux store에서 사용자 정보 및 추천 사용자 데이터 가져오기
  const userInfo = useSelector(selectUserInfo);
  const apiBaseUrl = useSelector(getApiBaseUrl);
  const { items, loading, error, pagination } = useSelector(selectRecommendedUsers);
  
  // 상태 관리
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10; // 페이지당 표시할 항목 수

  // API 호출 중복 방지 ref - 전역 중복 방지
  const fetchInProgressRef = useRef(false);
  const lastFetchParamsRef = useRef<string>('');
  const renderCountRef = useRef(0);
  const mountedRef = useRef(true);
  
  // 컴포넌트 언마운트 감지
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      fetchInProgressRef.current = false;
    };
  }, []);

  // 다국어 처리를 위한 훅 사용
  const { t } = useTranslation();
  
  // 렌더링 횟수 추적
  renderCountRef.current += 1;
  console.log(`[RecommendedUsers] 렌더링 #${renderCountRef.current}`, {
    userInfo: !!userInfo,
    currentPage,
    limit,
    loading,
    items: items?.length
  });
  
  // 컴포넌트 속성에서 텍스트 가져오기
  const title = componentProps.title || t('피추천인 목록');
  const emptyText = componentProps.emptyText || t('데이터가 없습니다');
  const loadingText = componentProps.loadingText || t('로딩 중...');
  const loginRequiredText = componentProps.loginRequiredText || t('로그인이 필요합니다.');

  // 페이지 변경 핸들러 (메모이제이션)
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // userInfo의 ID만 추출하여 의존성 안정화
  const userId = userInfo?.id;
  
  // 추천 사용자 데이터 가져오기 - 중복 방지 로직 적용
  useEffect(() => {
    console.log(`[RecommendedUsers] useEffect 실행`, {
      userId,
      currentPage,
      limit,
      fetchInProgress: fetchInProgressRef.current,
      lastParams: lastFetchParamsRef.current
    });
    
    if (!userId) {
      console.log(`[RecommendedUsers] userId 없음 - API 호출 스킵`);
      return;
    }
    
    // 현재 호출 파라미터 생성
    const currentParams = JSON.stringify({ page: currentPage, limit, userId });
    const globalKey = `recommended-users-${currentParams}`;
    
    // 전역 중복 방지 체크
    if (globalFetchMap.get(globalKey)) {
      console.log(`[RecommendedUsers] 전역 중복 방지 - 스킵`, globalKey);
      return;
    }
    
    // 로컬 중복 방지 체크
    if (fetchInProgressRef.current) {
      console.log(`[RecommendedUsers] 로컬 호출 중 - 스킵`);
      return;
    }
    
    if (lastFetchParamsRef.current === currentParams) {
      console.log(`[RecommendedUsers] 같은 파라미터로 이미 호출함 - 스킵`, currentParams);
      return;
    }
    
    // API 호출 시작 표시 (전역 + 로컬)
    globalFetchMap.set(globalKey, true);
    fetchInProgressRef.current = true;
    lastFetchParamsRef.current = currentParams;
    
    console.log(`[RecommendedUsers] API 호출 시작`, currentParams);
    
    dispatch(fetchRecommendedUsers({ page: currentPage, limit }))
      .finally(() => {
        // 호출 완료 후 플래그 정리
        if (mountedRef.current) {
          fetchInProgressRef.current = false;
        }
        globalFetchMap.delete(globalKey);
        console.log(`[RecommendedUsers] API 호출 완료`, currentParams);
      });
  }, [dispatch, userId, currentPage, limit]);

  // 컨테이너 스타일 (기본 스타일과 전달받은 스타일 병합)
  const containerStyle = useMemo(() => ({
    ...style
  }), [style]);

  // 로그인이 안된 경우 처리
  if (!userInfo) {
    return (
      <div 
        className={`recommended-users-container ${className}`}
        style={containerStyle}
        id={id}
        {...htmlProps}
      >
        <h2 className="recommended-users-title">{t(title)}</h2>
        <div className="recommended-users-login-required">
          <p>{t(loginRequiredText)}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`recommended-users-container ${className}`}
      style={containerStyle}
      id={id}
      {...htmlProps}
    >
      <h2 className="recommended-users-title">{t(title)}</h2>
      
      {loading && (
        <div className="recommended-users-loading">
          <div className="recommended-users-spinner"></div>
          <p>{t(loadingText)}</p>
        </div>
      )}
      
      {error && (
        <div className="recommended-users-error">
          <p>{t(error)}</p>
        </div>
      )}
      
      {!loading && !error && (
        <>
          <div className="recommended-users-info">
            <p>{t('총')} {pagination.totalCount} {t('명')}</p>
          </div>
          
          {items.length === 0 ? (
            <div className="recommended-users-empty">
              <p>{t(emptyText)}</p>
            </div>
          ) : (
            <div className="recommended-users-list">
              {items.map((user) => (
                <div key={user.id} className="recommended-users-item">
                  <div>
                    <p className="recommended-users-name">{user.insungId} ({user.name})</p>
                    <p className="recommended-users-date">{formatDate(user.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {pagination.totalCount > limit && (
            <div className="recommended-users-pagination">
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i + 1)}
                  className={`recommended-users-page-button ${currentPage === i + 1 ? 'active' : ''}`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecommendedUsersComponent; 